import React, { useEffect, useMemo, useState } from "react";
import {
    MaterialReactTable,
    useMaterialReactTable,
} from "material-react-table";
import { Box, IconButton, Tooltip, TextField, Button } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from '@mui/icons-material/Delete';
import axiosHttp from "../../services/interceptor";

const BASE_URL = process.env.REACT_APP_BASE_URL;

const ListVesselTable = ({ selectedVessels, setSelectedVessels, vesselMstId }) => {
    const [tableData, setTableData] = useState([]);
    const [rowCount, setRowCount] = useState(0);
    const [search, setSearch] = useState("");
    const [searchKey, setSearchKey] = useState("");
    const [loading, setLoading] = useState(false);

    const [pagination, setPagination] = useState({
        pageIndex: 0,
        pageSize: 10,
    });
    const [previousSearch, setPreviousSearch] = useState("");

    useEffect(() => {
        if (search.trim() === "") {
            if (searchKey !== "") {
                setSearchKey("");
                setPagination((prev) => ({ ...prev, pageIndex: 0 }));
            }
        }
    }, [search]);

    useEffect(() => {
        if (searchKey !== previousSearch) {
            setPreviousSearch(searchKey);
            setPagination({ ...pagination, pageIndex: 0 });
        }
        fetchVessels();
    }, [pagination.pageIndex, pagination.pageSize, searchKey]);


    const fetchVessels = async () => {
        setLoading(true);

        let payload = {
            globalFilter: searchKey,
            filters: [],
            pagination: {
                page: pagination.pageIndex + 1,
                pageSize: pagination.pageSize,
            },
            sortOption: {
                sortField: "ModifiedDate",
                isAscending: false,
            },
        };

        try {
            const response = await axiosHttp.post(
                `${BASE_URL}api/MyFleet/GetMyFleet`,
                payload
            );

            const data = response?.data?.Data?.Data || [];
            const total = response?.data?.Data?.Pagination?.TotalItems || 0;
            const filteredVessels = data?.filter((vessel) => {
                return vessel.VesselMstId !== vesselMstId
            });
            setTableData(filteredVessels);
            setRowCount(total);
        } catch (err) {
            console.error("Error fetching vessels", err);
            setTableData([]);
        }

        setLoading(false);
    };

    useEffect(() => {
        fetchVessels();
    }, [pagination.pageIndex, pagination.pageSize, searchKey]);



    const columns = useMemo(
        () => [
            {
                header: "",
                accessorKey: "action",
                enableSorting: false,
                enableColumnActions: false,
                enableColumnFilters: false,
                size: 20,
                Cell: ({ row }) => (
                    <Tooltip title="Add Vessel">
                        <IconButton
                            color="primary"
                            onClick={() => {
                                const { ImoNumber, WcnNumber, VesselName, CompanyName, VesselMstId } = row.original;
                                const alreadyExists = selectedVessels.some(
                                    (v) => v.imo === ImoNumber && v.wcn === WcnNumber
                                );
                                if (!alreadyExists) {
                                    setSelectedVessels([
                                        ...selectedVessels,
                                        {
                                            imo: ImoNumber,
                                            vesselName: row.original.VesselName,
                                            wcn: row.original.WcnNumber,
                                            company: row.original.CompanyName,
                                            vesselMstId: row.original.VesselMstId,
                                        },
                                    ]);
                                }
                            }}
                            sx={{
                                padding: "3px",
                                border: "1px solid lightgray",
                                borderRadius: "6px",
                            }}
                        >
                            <AddIcon sx={{ color: "#002a4e", fontSize: 18, fontWeight: "bold" }} />
                        </IconButton>
                    </Tooltip >
                ),
            },
            {
                header: "IMO Number", accessorKey: "ImoNumber", enableSorting: false,
                enableColumnActions: false,
                enableColumnFilters: false
            },
            {
                header: "Vessel Name", accessorKey: "VesselName", enableSorting: false,
                enableColumnActions: false,
                enableColumnFilters: false
            },
            {
                header: "Submitter WCN", accessorKey: "WcnNumber", enableSorting: false,
                enableColumnActions: false,
                enableColumnFilters: false
            },
            {
                header: "Company Name", accessorKey: "CompanyName", enableSorting: false,
                enableColumnActions: false,
                enableColumnFilters: false
            },
            {
                header: "IMO Vessel Type", accessorKey: "ImoVesselTypeName", enableSorting: false,
                enableColumnActions: false,
                enableColumnFilters: false
            },
        ],
        [selectedVessels]
    );


    const table = useMaterialReactTable({
        columns,
        data: tableData,
        manualPagination: true,
        rowCount,
        state: {
            isLoading: loading,
            pagination,
        },
        onPaginationChange: setPagination,
        enableSorting: false,
        enableColumnFilters: false,
        enableTopToolbar: false,

        muiPaginationProps: {
            rowsPerPageOptions: [10, 25, 50],
            showFirstButton: true,
            showLastButton: true,
        },

        muiTableHeadCellProps: {
            sx: {
                backgroundColor: "#042a4c",
                fontWeight: "bold",
                color: "#ffffff",
                fontSize: "12px",
                paddingY: "3px",
            },
        },

        muiTableBodyRowProps: {
            sx: { height: "24px" },
        },

        muiTableBodyCellProps: {
            sx: {
                paddingY: "2px",
                paddingX: "6px",
                fontSize: "12px",
                lineHeight: "1.2",
            },
        },
    });


    return (
        <>
            <Box sx={{ mb: 2, display: "flex", gap: 1 }}>
                <TextField
                    placeholder="Search by IMO Number or Vessel Name"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    size="small"
                    fullWidth
                />

                <Button
                    variant="contained"
                    onClick={() => {
                        setPagination({ ...pagination, pageIndex: 0 });
                        setSearchKey(search);
                    }}
                    sx={{ backgroundColor: "#042a4c" }}
                >
                    Search
                </Button>
            </Box>

            <MaterialReactTable table={table} />

            {selectedVessels.length > 0 && (
                <div
                    className="mt-3 p-3 border rounded"
                    style={{ backgroundColor: "#fff", fontSize: "12px" }}
                >
                    <strong style={{ fontSize: "13px", color: "#042a4c" }}>
                        Added Vessels
                    </strong>

                    <div
                        style={{
                            maxHeight: "150px",
                            overflowY: "auto",
                            marginTop: "8px",
                            paddingRight: "5px"
                        }}
                    >
                        {selectedVessels.map((v) => (
                            <div
                                key={v.imo}
                                className="d-flex justify-content-between align-items-center mt-2 p-1"
                                style={{ height: "26px", fontSize: "12px" }}
                            >
                                <span>
                                    <strong style={{ color: "#042a4c" }}>
                                        {v.imo} – {v.vesselName} – {v.wcn}, {v.company}
                                    </strong>
                                </span>
                                <Tooltip title="Remove Vessel">
                                    <IconButton
                                        size="small"
                                        color="error"
                                        sx={{ padding: "2px" }}
                                        onClick={() =>
                                            setSelectedVessels(
                                                selectedVessels.filter(
                                                    (item) =>
                                                        !(item.imo === v.imo && item.wcn === v.wcn)
                                                )
                                            )
                                        }
                                    >
                                        <DeleteIcon style={{ fontSize: "16px" }} />
                                    </IconButton>
                                </Tooltip>
                            </div>
                        ))}
                    </div>
                </div>
            )}

        </>
    );
};

export default ListVesselTable;
